import { CloseOutlined } from '@ant-design/icons';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import {
  activeCompanySelector,
  selectActiveCompanyId,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './BillingPlans.module.css';
import { uniqueId } from 'lodash';
import { useOpenConditionalDowngradeModal } from '../../../../../common/hooks/use-open-conditional-downgrade-modal';
import {
  AddonSettingsKeyType,
  EnhancedPlan,
} from '@mm/state/interfaces/subscription';
import { BillingPlanCardProps, useBillingPlans } from './use-billing-plans';
import { Divider } from 'antd';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { isBilling2023V2Subscription } from '@mm/state/model/SubscriptionModel';
import { ContactsManagementDescription } from './ContactsManagementDescription';
import { useResolutionInfo } from '@mm/hooks';
import { TABLET_SMALL } from '../../../../../editor/constants/screenSize';
import { Plan } from './Plan';
import { BillingPlansVerticalLayout } from './BillingPlansVerticalLayout';
import { PlanDescription } from './PlanDescription';

type Props = {
  className?: string;
  landmineOpenId?: number;
  onBillingSuccess?: () => void;
  hideFreePlan?: boolean;
  hideMonthlyManagementFee?: boolean;
  whatRowAlwaysVisible?: boolean;
  costPerContactRowAlwaysVisible?: boolean;
  plans?: BillingPlanCardProps[]; // prop for controlled version
};

export const BillingPlans = ({
  className,
  plans,
  landmineOpenId,
  onBillingSuccess,
  hideFreePlan,
  hideMonthlyManagementFee,
  whatRowAlwaysVisible,
  costPerContactRowAlwaysVisible,
}: Props) => {
  const isShopify = useSelector(selectIsShopifyContext);
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const companyId = useSelector(selectActiveCompanyId);
  const company = useSelector(activeCompanySelector);
  const isAgency = company?.companyAttributes?.agency;
  const [modalProps, setModalProps] = useState<
    { plan?: EnhancedPlan; type: AddonSettingsKeyType } | undefined
  >(undefined);

  const {
    billingPlans,
    activeFacebookPage,
    currentPlan,
    tabId,
    pageId,
    plansToDowngrade,
    canCancelSubscription,
    isAwaitingCancellation,
    isManageSubscriptionsPage,
  } = useBillingPlans({
    isAnnual: false,
    onBillingSuccess,
    hideFreePlan,
    landmineOpenId,
  });
  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();
  const { getPlans } = useSubscriptionCRUDActions();
  const filteredOptions = plans || billingPlans.filter(option => !option.hide);
  const { subscription } = activeFacebookPage || {};
  const isBillingV2Sub =
    subscription && isBilling2023V2Subscription(subscription);
  const { openConditionalDowngradeModal } = useOpenConditionalDowngradeModal();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;

  const onCancelSubscription = () => {
    if (subscription && currentPlan) {
      openConditionalDowngradeModal({
        subscription,
        plan: currentPlan,
        facebookPage: activeFacebookPage,
        tabId,
      });
    }
  };

  const getOptionParams = (option: BillingPlanCardProps) => {
    const downgrade = plansToDowngrade.find(plan =>
      option?.planSubtypes?.every(subtype =>
        plan?.planSubtypes.includes(subtype),
      ),
    );

    if (isShopify) {
      return {
        currentTag: option.currentTag,
        downgrade: undefined,
        buttonNameAdjusted: option.buttonName,
      };
    }

    const currentTag =
      currentPlan &&
      option?.planSubtypes?.every(subtype =>
        currentPlan?.planSubtypes.includes(subtype),
      );

    const buttonNameAdjusted = currentTag
      ? 'Current Plan'
      : downgrade
      ? 'Downgrade'
      : option.buttonName;

    return {
      downgrade,
      currentTag,
      buttonNameAdjusted,
    };
  };

  useEffect(() => {
    if (!companyId) {
      return;
    }
    (async () => {
      if (!plansMapping) {
        await getPlans(companyId);
      }
    })();
  }, [plansMapping, companyId, getPlans]);

  const cancelSubscription =
    canCancelSubscription &&
    (isAwaitingCancellation ? (
      <div className={styles.cancellationInfo}>
        Awaiting cancellation approval
      </div>
    ) : (
      <PrimaryButton
        onClick={onCancelSubscription}
        className={styles.cancelButton}
        color="transparent"
      >
        <CloseOutlined />
        Cancel your plan
      </PrimaryButton>
    ));

  if (isMobile) {
    return (
      <BillingPlansVerticalLayout
        options={filteredOptions}
        getOptionParams={getOptionParams}
      />
    );
  }

  return (
    <div id="billing-plans" className={classNames(className, styles.container)}>
      {cancelSubscription}

      <div
        className={classNames(styles.content, {
          [styles.contentWhite]: isManageSubscriptionsPage,
          [styles.contentWithTag]: isFreeTrialPromotionActive,
          [styles.agencyContent]: isAgency,
        })}
      >
        <div id="billing-plans__row-plans" className={styles.row}>
          <div className={styles.rowLabel}>Our plans</div>
          <Divider type="vertical" className={styles.divider} />
          {filteredOptions.map((option, index) => {
            const nextOption = filteredOptions?.[index + 1];
            const isCustom = option?.isCustom || nextOption?.isCustom;
            return (
              <React.Fragment key={uniqueId()}>
                <Plan
                  option={option}
                  getOptionParams={getOptionParams}
                  className={classNames({
                    [styles.widerMainBlock]: filteredOptions.length <= 2,
                  })}
                />
                {!isCustom && (
                  <Divider type="vertical" className={styles.divider} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div id="billing-plans__row-what" className={styles.row}>
          <div className={styles.rowLabel}>What you get?</div>
          <Divider type="vertical" className={styles.divider} />
          {filteredOptions.map((option, index) => {
            const nextOption = filteredOptions?.[index + 1];
            const isCustom = option?.isCustom || nextOption?.isCustom;
            const { currentTag } = getOptionParams(option);
            return currentTag && !whatRowAlwaysVisible && !isBillingV2Sub ? (
              <React.Fragment key={uniqueId()}>
                <div
                  className={classNames(styles.points, {
                    [styles.widerPlanDescription]: filteredOptions.length <= 2,
                  })}
                />
                {!isCustom && (
                  <Divider type="vertical" className={styles.divider} />
                )}
              </React.Fragment>
            ) : (
              <React.Fragment key={uniqueId()}>
                <PlanDescription option={option} />
                {!isCustom && (
                  <Divider type="vertical" className={styles.divider} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <Divider
          className={classNames(styles.dividerHorizontal, {
            [styles.dividerHorThreeOpt]: filteredOptions.length === 3,
            [styles.dividerHorAuto]: filteredOptions.length <= 2,
          })}
        />
        <div id="billing-plans__row-cost-per-contact" className={styles.row}>
          <div className={styles.rowLabel}>Cost Per Contact</div>
          <Divider type="vertical" className={styles.divider} />
          {filteredOptions.map(option => {
            const { isCustom } = option || {};
            const { currentTag } = getOptionParams(option);
            return currentTag &&
              !costPerContactRowAlwaysVisible &&
              !isBillingV2Sub ? (
              <React.Fragment key={uniqueId()}>
                <div
                  className={classNames(styles.points, {
                    [styles.widerPlanDescription]: filteredOptions.length <= 2,
                  })}
                />
                {!isCustom && (
                  <Divider type="vertical" className={styles.divider} />
                )}
              </React.Fragment>
            ) : (
              <React.Fragment key={uniqueId()}>
                <div
                  id={
                    option.planCode
                      ? `billing-plans__cost-${option.planCode}`
                      : undefined
                  }
                  className={classNames(styles.costs, {
                    [styles.costsVerticalLayout]: isMobile,
                  })}
                >
                  {Array.isArray(option.costs)
                    ? option.costs?.map(cost => (
                        <div key={uniqueId()} className={styles.cost}>
                          {cost}
                        </div>
                      ))
                    : option.costs}
                </div>
                {!isCustom && (
                  <Divider type="vertical" className={styles.divider} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        {!hideMonthlyManagementFee && (
          <>
            <Divider
              className={classNames(styles.dividerHorizontal, {
                [styles.dividerHorThreeOpt]: filteredOptions.length === 3,
                [styles.dividerHorAuto]: filteredOptions.length <= 2,
              })}
            />
            <div className={styles.row}>
              <div className={styles.rowLabel}>
                Monthly contact management fee
              </div>
              <Divider type="vertical" className={styles.divider} />
              {(filteredOptions.find(opt => opt.isFreeTrial) ||
                (currentPlan && !isBillingV2Sub)) && (
                <div
                  className={classNames(styles.points, {
                    [styles.widerPlanDescription]: filteredOptions.length <= 2,
                  })}
                />
              )}
              <ContactsManagementDescription
                css={{
                  container: classNames({
                    [styles.column]: filteredOptions.length <= 3,
                    [styles.paddingLeft]:
                      !currentPlan || (currentPlan && isBillingV2Sub),
                  }),
                  content: filteredOptions.length <= 3 && styles.fullWidth,
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
